const MARKETING_ENABLED = process.env.REACT_APP_MARKETING_ANALYTICS_ENABLED === "true";
const GOOGLE_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";
const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || "";
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";
const ATTRIBUTION_KEY = "dteflow_marketing_attribution";
const CONSENT_KEY = "dteflow_marketing_consent";
const CONSENT_EVENT = "dteflow:marketing-consent";
const META_PIXEL_INITIALIZED_KEY = "__DTE_FLOW_META_PIXEL_INITIALIZED__";
const META_SCRIPT_ID = "dteflow-meta-pixel";
const META_SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js";
let initialized = false;

function canUseBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function readStorage() {
  if (!canUseBrowser()) return {};
  try {
    return JSON.parse(window.sessionStorage.getItem(ATTRIBUTION_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function writeStorage(value) {
  if (!canUseBrowser()) return;
  try {
    window.sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(value));
  } catch {
    // Tracking must never affect navigation when storage is unavailable.
  }
}

export function getMarketingConsent() {
  if (!canUseBrowser()) return null;
  try {
    const value = window.localStorage.getItem(CONSENT_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

export function setMarketingConsent(value) {
  if (!canUseBrowser() || !["granted", "denied"].includes(value)) return;
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // Consent storage must never block the application.
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { value } }));
}

export function resetMarketingConsent() {
  if (!canUseBrowser()) return;
  try {
    window.localStorage.removeItem(CONSENT_KEY);
  } catch {
    // Consent storage must never block the application.
  }
  initialized = false;
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { value: null } }));
}

export function subscribeToMarketingConsent(listener) {
  if (!canUseBrowser()) return () => {};
  window.addEventListener(CONSENT_EVENT, listener);
  return () => window.removeEventListener(CONSENT_EVENT, listener);
}

export function captureAttribution() {
  if (!canUseBrowser()) return {};
  const params = new URLSearchParams(window.location.search);
  const current = readStorage();
  const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "gclid", "fbclid", "ttclid"];
  const next = { ...current };
  keys.forEach((key) => {
    const value = params.get(key);
    if (value) next[key] = value.slice(0, 200);
  });
  if (!next.landing_path) next.landing_path = window.location.pathname;
  if (!next.first_seen_at) next.first_seen_at = new Date().toISOString();
  writeStorage(next);
  return next;
}

function appendScript(id, src) {
  if (!canUseBrowser()) return false;
  if (document.getElementById(id)) return true;
  const existingScript = Array.from(document.scripts).find((script) => script.src === src);
  if (existingScript) return true;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
  return true;
}

function inferProduct(name, params) {
  if (params.product) return params.product;
  return name.includes("commercial") ? "comercial" : "fiscal";
}

function inferDestination(name, params) {
  if (params.destination) return params.destination;
  return name.includes("app_store") ? "play" : "web";
}

function initializeGoogle() {
  const googleDestinations = [GOOGLE_MEASUREMENT_ID, GOOGLE_ADS_ID].filter(Boolean);
  if (!googleDestinations.length || !canUseBrowser()) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  googleDestinations.forEach((destinationId) => {
    window.gtag("config", destinationId, { send_page_view: false, anonymize_ip: true });
  });
  // One Google tag can send the same consented signal to GA4 and Google Ads.
  // Conversions remain sourced from the GA4 `sign_up` key event to avoid duplicates.
  appendScript("dteflow-ga4", `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleDestinations[0])}`);
}

function initializeMeta() {
  if (!META_PIXEL_ID || !canUseBrowser()) return;
  if (!window.fbq) {
    const fbq = function fbq() { fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments); };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    window.fbq = fbq;
  }
  // Keep the application-owned initialization idempotent across rerenders.
  // Meta's Event Setup Tool may inject its own runtime separately; this guard
  // prevents DTE Flow from adding another initialization of its own.
  if (window[META_PIXEL_INITIALIZED_KEY] !== META_PIXEL_ID) {
    window.fbq("init", META_PIXEL_ID);
    window[META_PIXEL_INITIALIZED_KEY] = META_PIXEL_ID;
  }
  // Reuse a script injected by Meta's Event Setup Tool instead of adding a
  // second fbevents.js runtime. This keeps the production page single-source
  // while still allowing the tool to inspect events during setup.
  appendScript(META_SCRIPT_ID, META_SCRIPT_SRC);
}

export function initializeMarketingAnalytics() {
  if (!MARKETING_ENABLED || !canUseBrowser() || initialized || getMarketingConsent() !== "granted") return false;
  initialized = true;
  captureAttribution();
  initializeGoogle();
  initializeMeta();
  return true;
}

export function trackMarketingEvent(name, params = {}) {
  if (!MARKETING_ENABLED || !canUseBrowser() || getMarketingConsent() !== "granted") return;
  initializeMarketingAnalytics();
  const product = inferProduct(name, params);
  const destination = inferDestination(name, params);
  const payload = { ...params, product, destination, ...readStorage() };
  if (typeof window.gtag === "function") window.gtag("event", name, payload);
  if (typeof window.fbq === "function") {
    const metaEvent = name === "landing_view"
      ? "PageView"
      : name === "signup_start"
        ? "Lead"
        : name === "sign_up"
          ? "CompleteRegistration"
          : ["app_store_click", "commercial_app_store_click"].includes(name)
            ? "ViewContent"
            : "CustomEvent";
    window.fbq("track", metaEvent, {
      event_name: name,
      placement: params.placement || "landing",
      product,
      destination,
    });
  }
}

export function getMarketingAttribution() {
  return readStorage();
}
